import React from 'react'
// @src imports

import { textEditor } from 'src/helpers'
import { signatureCharacter } from './ElementText'
import { ComputedElementText } from './types'
import { rgbString } from '../../redux/selectors/text'
import { useEffect, useRef, useState } from 'src/hooks'

import { Api } from '../api'
import { Steps } from '../types'

const fragmentsFormat = 'application/vnd.sendoutcards.textfragments+json'

const selectionIndex = (
  node: Node,
  offset: number,
  isStart: boolean,
  selectionUpperBound: number,
) => {
  if (node.nodeName === '#text') {
    const spanIndex = parseInt(
      (node.parentElement && node.parentElement.dataset.index) || '1',
      10,
    )
    if (offset === 0) {
      return spanIndex
    } else if (offset === (node.textContent && node.textContent.length)) {
      return spanIndex + 1
    } else {
      return isStart ? spanIndex : spanIndex + 1
    }
  } else if (node.nodeName === 'P' && node instanceof HTMLElement) {
    return parseInt(node.dataset.index || '1', 10) + offset + 1
  } else {
    return isStart ? 1 : selectionUpperBound
  }
}

type TextEditorProps = {
  textEditorId: string
  elementText: ComputedElementText
  selectText: (start: number, end: number) => void
  fontScale: number
  api: Api
}

const TextEditor: React.FC<TextEditorProps> = props => {
  const { textEditorId, elementText, selectText, fontScale, api } = props
  const { paragraphs, selectionUpperBound, selection } = elementText
  const { updateText, step } = api
  const utils =
    step.type === Steps.EditText ? textEditor(updateText, step) : undefined

  const mutableRef = useRef<HTMLDivElement>()
  const ref = mutableRef.current
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (!ref || !selection) {
      return
    }

    const startNode = ref.childNodes[selection.start.paragraph]
    const endNode = ref.childNodes[selection.end.paragraph]

    if (!startNode || !endNode) {
      return
    }

    const range = document.createRange()

    range.setStart(
      startNode,
      Math.max(
        0,
        Math.min(selection.start.offset, startNode.childNodes.length),
      ),
    )

    range.setEnd(
      endNode,
      Math.max(0, Math.min(selection.end.offset, endNode.childNodes.length)),
    )

    const windowSelection = window.getSelection()

    if (windowSelection == null) {
      return
    }

    windowSelection.removeAllRanges()
    windowSelection.addRange(range)
  }, [ref, selection, isFocused, textEditorId])

  const fontFamily = (fontId: string): string =>
    (
      api.fonts.fonts.find(font => font.id === fontId) ||
      api.fonts.signatures.find(font => font.id === fontId) || {
        fontName: 'HelveticaV2',
      }
    ).fontName

  const copy = (event: React.ClipboardEvent) => {
    if (!selection) {
      return
    }
    event.preventDefault()
    event.clipboardData.setData('text', selection.text)
    event.clipboardData.setData(
      fragmentsFormat,
      JSON.stringify(selection.fragments),
    )
  }

  const saveSelected = () => {
    const windowSelection = window.getSelection()

    if (windowSelection == null) {
      return
    }

    const range = windowSelection.getRangeAt(0)

    const start = selectionIndex(
      range.startContainer,
      range.startOffset,
      true,
      selectionUpperBound,
    )

    const end = selectionIndex(
      range.endContainer,
      range.endOffset,
      false,
      selectionUpperBound,
    )

    if (
      !selection ||
      selection.start.index !== start ||
      selection.end.index !== end
    ) {
      selectText(start, end)
    }
  }

  return (
    <div
      id={textEditorId}
      ref={ref => (mutableRef.current = ref ?? undefined)}
      contentEditable={true}
      suppressContentEditableWarning={true}
      spellCheck="false"
      autoCapitalize="off"
      autoCorrect="off"
      style={{
        whiteSpace: 'pre-wrap',
        outline: '0px',
        width: '100%',
      }}
      onClick={event => {
        event.stopPropagation()
        event.preventDefault()
        saveSelected()
      }}
      onCut={event => {
        copy(event)
        utils?.deleteFragments()
      }}
      onCopy={copy}
      onPaste={event => {
        event.preventDefault()
        const fragments = event.clipboardData.getData(fragmentsFormat)
        const text = event.clipboardData.getData('text')
        if (fragments) {
          utils?.insertFragments(JSON.parse(fragments))
        } else if (text) {
          utils?.insertText(text)
        }
      }}
      onKeyDown={event => {
        if (event.key === 'Backspace') {
          event.preventDefault()
          utils?.deleteFragments()
        } else if (event.key === 'Delete') {
          event.preventDefault()
          utils?.deleteForwardFragments()
        } else if (event.key === 'Enter') {
          event.preventDefault()
          utils?.insertNewLine()
        } else if (event.key === 'Tab') {
          event.preventDefault()
          utils?.insertCharacter('\t')
        }
      }}
      onKeyPress={event => {
        if (event.nativeEvent.metaKey || event.nativeEvent.ctrlKey) {
          saveSelected()
          return
        }
        event.preventDefault()
        utils?.insertCharacter(event.nativeEvent.key)
        saveSelected()
      }}
      onSelect={() => saveSelected()}
      onFocus={() => {
        setIsFocused(true)
      }}
      onBlur={() => {
        // when there's an active selection do not force focus
        if (selection && selection.start.index !== selection.end.index) {
          return
        }
        setIsFocused(false)
      }}
    >
      {paragraphs.map(paragraph => (
        <p
          key={paragraph.index}
          data-index={paragraph.index}
          style={{
            fontFamily: fontFamily(paragraph.fontId),
            fontSize: paragraph.fontSize * fontScale,
            color: rgbString(paragraph.color),
            textAlign: paragraph.textAlign.toLowerCase() as
              | 'left'
              | 'center'
              | 'right',
            lineHeight: 1.4,
            minHeight: '1.4em',
            margin: '0',
          }}
        >
          {paragraph.fragments.map(fragment => (
            <span
              key={fragment.index}
              data-index={fragment.index}
              style={{
                fontFamily: fontFamily(
                  fragment.signatureFontId || fragment.fontId,
                ),
                fontSize: fragment.fontSize * fontScale,
                color: rgbString(fragment.color),
                textDecoration:
                  fragment.text && fragment.text.length > 1
                    ? 'underline'
                    : 'none',
                cursor: 'text',
              }}
            >
              {fragment.text ?? signatureCharacter}
            </span>
          ))}
        </p>
      ))}
    </div>
  )
}

export default TextEditor
